import { useCallback, useEffect, useRef, useState } from 'react';

import { describeApiError } from '../lib/api-client';
import { useApiClient } from '../providers/auth-provider';

import type {
  RepositorySkill,
  RepositorySkillFile,
  RepositorySkillVersion,
} from '../lib/api-client';

export type AsyncStatus = 'idle' | 'loading' | 'success' | 'error';

const PAGE_SIZE = 25;

// ---- pure helpers (unit-tested in use-skills-repository.test.ts) ----

/**
 * `mode` arrives as an octal STRING ("0644"). `Number("0644")` is DECIMAL 644,
 * and `644 & 0o111` happens to be 0 while `755 & 0o111` happens to be non-zero —
 * so the naive check is right for the two common modes by coincidence and
 * wrong for 0444 / 0664 / 0600. Parse base 8.
 */
export function isExecutableMode(mode: string): boolean {
  const bits = Number.parseInt(mode, 8);
  return Number.isFinite(bits) && (bits & 0o111) !== 0;
}

/** Whether the server's content type is something we can show as text. */
export function isTextContentType(contentType: string): boolean {
  const type = (contentType.split(';')[0] ?? '').trim().toLowerCase();
  if (type.startsWith('text/')) return true;
  if (type.endsWith('+json') || type.endsWith('+xml')) return true;
  return [
    'application/json',
    'application/xml',
    'application/yaml',
    'application/x-yaml',
    'application/toml',
    'application/javascript',
    'application/typescript',
    'application/x-sh',
    'application/x-shellscript',
  ].includes(type);
}

/** Append a page, dropping ids already present (a `starting_after` page can overlap after a mutation). */
export function appendPage<T extends { id: string }>(prev: T[], next: T[]): T[] {
  const seen = new Set(prev.map((item) => item.id));
  return [...prev, ...next.filter((item) => !seen.has(item.id))];
}

/** The file to open first: SKILL.md when present, else the first indexed file. */
export function preferredFile(files: readonly RepositorySkillFile[]): string | null {
  return (files.find((f) => f.path === 'SKILL.md') ?? files[0])?.path ?? null;
}

/** Decode bytes as UTF-8 text when the type is textual and the bytes are valid; `null` means "binary". */
export function decodeFileText(bytes: Uint8Array, contentType: string): string | null {
  if (!isTextContentType(contentType)) return null;
  try {
    return new TextDecoder('utf-8', { fatal: true }).decode(bytes);
  } catch {
    return null;
  }
}

// ---- ViewModels ----

/**
 * List → detail ViewModel for the Skills Repository view. Mutations patch the
 * local list with the server's response so the row and the detail never
 * disagree with what was just persisted.
 */
export function useSkillsRepository() {
  const apiClient = useApiClient();
  const [skills, setSkills] = useState<RepositorySkill[]>([]);
  const [status, setStatus] = useState<AsyncStatus>('idle');
  const [error, setError] = useState<string | undefined>();
  const [hasMore, setHasMore] = useState(false);

  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [detail, setDetail] = useState<RepositorySkill | null>(null);
  const [detailStatus, setDetailStatus] = useState<AsyncStatus>('idle');
  const [detailError, setDetailError] = useState<string | undefined>();
  const detailGeneration = useRef(0);

  const refresh = useCallback(async () => {
    setStatus('loading');
    setError(undefined);
    try {
      const page = await apiClient.listRepositorySkills({ limit: PAGE_SIZE });
      setSkills(page.data);
      setHasMore(page.has_more);
      setStatus('success');
    } catch (err: unknown) {
      setError(describeApiError(err, 'Failed to load skills'));
      setStatus('error');
    }
  }, [apiClient]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const loadMore = useCallback(async () => {
    const last = skills[skills.length - 1];
    if (!last || !hasMore) return;
    const page = await apiClient.listRepositorySkills({ limit: PAGE_SIZE, starting_after: last.id });
    setSkills((prev) => appendPage(prev, page.data));
    setHasMore(page.has_more);
  }, [apiClient, skills, hasMore]);

  const loadDetail = useCallback(
    async (id: string) => {
      const gen = ++detailGeneration.current;
      setDetailStatus('loading');
      setDetailError(undefined);
      try {
        const loaded = await apiClient.getRepositorySkill(id);
        if (gen !== detailGeneration.current) return;
        setDetail(loaded);
        setDetailStatus('success');
      } catch (err: unknown) {
        if (gen !== detailGeneration.current) return;
        setDetailError(describeApiError(err, 'Failed to load skill'));
        setDetailStatus('error');
      }
    },
    [apiClient],
  );

  useEffect(() => {
    if (!selectedId) {
      detailGeneration.current += 1;
      setDetail(null);
      setDetailStatus('idle');
      setDetailError(undefined);
      return;
    }
    void loadDetail(selectedId);
  }, [selectedId, loadDetail]);

  const upload = useCallback(
    async (bundle: File, summary: string): Promise<RepositorySkill> => {
      const created = await apiClient.uploadRepositorySkill(bundle, summary || undefined);
      setSkills((prev) => [created, ...prev.filter((s) => s.id !== created.id)]);
      setSelectedId(created.id);
      void loadDetail(created.id);
      return created;
    },
    [apiClient, loadDetail],
  );

  const uploadVersion = useCallback(
    async (id: string, bundle: File, summary: string): Promise<RepositorySkillVersion> => {
      const version = await apiClient.uploadRepositorySkillVersion(id, bundle, summary || undefined);
      setSkills((prev) =>
        prev.map((s) => (s.id === id ? { ...s, current_version: version, updated_at: version.created_at } : s)),
      );
      await loadDetail(id);
      return version;
    },
    [apiClient, loadDetail],
  );

  const setCurrentVersion = useCallback(
    async (id: string, versionId: string): Promise<RepositorySkill> => {
      const updated = await apiClient.patchRepositorySkill(id, { current_version_id: versionId });
      setSkills((prev) => prev.map((s) => (s.id === id ? updated : s)));
      detailGeneration.current += 1;
      setDetail(updated);
      setDetailStatus('success');
      return updated;
    },
    [apiClient],
  );

  const remove = useCallback(
    async (id: string): Promise<void> => {
      await apiClient.deleteRepositorySkill(id);
      setSkills((prev) => prev.filter((s) => s.id !== id));
      setSelectedId((current) => (current === id ? null : current));
    },
    [apiClient],
  );

  return {
    skills,
    status,
    error,
    hasMore,
    loadMore,
    refresh,
    selectedId,
    setSelectedId,
    detail,
    detailStatus,
    detailError,
    upload,
    uploadVersion,
    setCurrentVersion,
    remove,
  };
}

/**
 * Version history of one skill, newest first. `refreshKey` should change when a
 * version is published or `current_version` moves, so the list refetches.
 */
export function useRepositorySkillVersions(skillId: string | null, refreshKey: string) {
  const apiClient = useApiClient();
  const [versions, setVersions] = useState<RepositorySkillVersion[]>([]);
  const [status, setStatus] = useState<AsyncStatus>('idle');
  const [error, setError] = useState<string | undefined>();
  const [hasMore, setHasMore] = useState(false);
  const generation = useRef(0);

  useEffect(() => {
    const gen = ++generation.current;
    if (!skillId) {
      setVersions([]);
      setStatus('idle');
      return;
    }
    setStatus('loading');
    setError(undefined);
    apiClient
      .listRepositorySkillVersions(skillId, { limit: PAGE_SIZE })
      .then((page) => {
        if (gen !== generation.current) return;
        setVersions(page.data);
        setHasMore(page.has_more);
        setStatus('success');
      })
      .catch((err: unknown) => {
        if (gen !== generation.current) return;
        setError(describeApiError(err, 'Failed to load versions'));
        setStatus('error');
      });
  }, [apiClient, skillId, refreshKey]);

  const loadMore = useCallback(async () => {
    const last = versions[versions.length - 1];
    if (!skillId || !last || !hasMore) return;
    const page = await apiClient.listRepositorySkillVersions(skillId, { limit: PAGE_SIZE, starting_after: last.id });
    setVersions((prev) => appendPage(prev, page.data));
    setHasMore(page.has_more);
  }, [apiClient, skillId, versions, hasMore]);

  return { versions, status, error, hasMore, loadMore };
}

export interface RepositorySkillFileContent {
  path: string;
  content_type: string;
  version_id: string;
  sha256: string;
  /** Decoded UTF-8 text, or `null` for a binary. */
  text: string | null;
}

/**
 * File index + one selected file of a specific version. Both requests carry a
 * generation guard so a slow response for a version the user already left
 * cannot overwrite the current one.
 */
export function useRepositorySkillFiles(skillId: string | null, versionId: string | null) {
  const apiClient = useApiClient();
  const [files, setFiles] = useState<RepositorySkillFile[]>([]);
  const [filesStatus, setFilesStatus] = useState<AsyncStatus>('idle');
  const [filesError, setFilesError] = useState<string | undefined>();
  const [selectedPath, setSelectedPath] = useState<string | null>(null);
  const [content, setContent] = useState<RepositorySkillFileContent | null>(null);
  const [fileStatus, setFileStatus] = useState<AsyncStatus>('idle');
  const [fileError, setFileError] = useState<string | undefined>();
  const filesGeneration = useRef(0);
  const fileGeneration = useRef(0);

  useEffect(() => {
    const gen = ++filesGeneration.current;
    if (!skillId || !versionId) {
      setFiles([]);
      setFilesStatus('idle');
      setSelectedPath(null);
      return;
    }
    setFilesStatus('loading');
    setFilesError(undefined);
    apiClient
      .getRepositorySkillVersion(skillId, versionId)
      .then((version) => {
        if (gen !== filesGeneration.current) return;
        const index = version.files ?? [];
        setFiles(index);
        setFilesStatus('success');
        setSelectedPath((prev) => (prev && index.some((f) => f.path === prev) ? prev : preferredFile(index)));
      })
      .catch((err: unknown) => {
        if (gen !== filesGeneration.current) return;
        setFilesError(describeApiError(err, 'Failed to load files'));
        setFilesStatus('error');
      });
  }, [apiClient, skillId, versionId]);

  useEffect(() => {
    const gen = ++fileGeneration.current;
    if (!skillId || !versionId || !selectedPath) {
      setContent(null);
      setFileStatus('idle');
      return;
    }
    setFileStatus('loading');
    setFileError(undefined);
    apiClient
      .getRepositorySkillFile(skillId, selectedPath, versionId)
      .then((raw) => {
        if (gen !== fileGeneration.current) return;
        setContent({
          path: selectedPath,
          content_type: raw.content_type,
          version_id: raw.version_id,
          sha256: raw.sha256,
          text: decodeFileText(raw.bytes, raw.content_type),
        });
        setFileStatus('success');
      })
      .catch((err: unknown) => {
        if (gen !== fileGeneration.current) return;
        setFileError(describeApiError(err, 'Failed to load file'));
        setFileStatus('error');
      });
  }, [apiClient, skillId, versionId, selectedPath]);

  const entry = selectedPath ? (files.find((f) => f.path === selectedPath) ?? null) : null;
  return { files, filesStatus, filesError, selectedPath, setSelectedPath, entry, content, fileStatus, fileError };
}
